import React, { FC, ReactNode } from "react";
import { Badge, HiddenVisually, Icon, Link, Text, Title } from "@bookingcom/bui-react";
import {
  ClockIcon,
  AirportIcon,
  PlaneTripIcon,
  BagHoldIcon,
  TravelInsuranceIcon
} from "@bookingcom/bui-assets-react/streamline/index.js";
import { useI18n, I18nChildContext } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { UITimelineLeg, UITimelineRow, UITimelineStop } from "@flights/types";
import { VI_TERMS_AND_CONDITIONS_LINK } from "../../../../constants";
import Frame from "../../Frame";
import styles from "./TimelineStop.module.css";
import { mcn } from "../../../../utils/mergeClassnames";
import { formattingUtils } from "@flights/web-api-utils-universal";
import useProfiler from "../../../../hooks/useProfiler";
import useFlightsFunnelWebCondensedItinerary from "../../../../utils/experiments/funnel/flights_funnel_web_condensed_itinerary";
import { type SVG } from "@bookingcom/bui-react/components/Icon/Icon.types";

export const TimelineStop: FC<{
  stop: UITimelineStop;
  prevLegData?: UITimelineRow;
  nextLegData?: UITimelineRow;
}> = ({ stop, prevLegData, nextLegData }) => {
  const i18n = useI18n() as I18nChildContext;
  const { durationMinutes, layoverType } = stop;

  const prevDestination = prevLegData ? (prevLegData as UITimelineLeg).to : undefined;
  const nextSource = nextLegData ? (nextLegData as UITimelineLeg).from : undefined;

  const virtualInterliningLegData = prevLegData ? (prevLegData as UITimelineLeg).vi : undefined;
  const isVirtualInterliningProtectedLeg = virtualInterliningLegData?.protected;

  const isVIChangeAirport = isVirtualInterliningProtectedLeg && virtualInterliningLegData?.airportChange;
  const isVICollectBaggage = isVirtualInterliningProtectedLeg && virtualInterliningLegData?.baggageCollection;

  const isRegularChangeOfAirports = layoverType === "CHANGE_OF_AIRPORTS";

  const shouldWarn = isVICollectBaggage || isVIChangeAirport;
  const shouldWarnDestructively = isRegularChangeOfAirports;
  const shouldShowVIChangeAirport = isVIChangeAirport && prevDestination && nextSource;

  const { isPb, isMobile } = useProfiler();
  const flights_funnel_web_condensed_itinerary = useFlightsFunnelWebCondensedItinerary(); //eslint-disable-line @typescript-eslint/naming-convention
  const isCondencedItinerary =
    !isVirtualInterliningProtectedLeg &&
    !(isRegularChangeOfAirports && !shouldShowVIChangeAirport) &&
    !isVirtualInterliningProtectedLeg &&
    !shouldShowVIChangeAirport &&
    !(isVICollectBaggage && prevDestination) &&
    layoverType !== "CHANGE_OF_GAUGE" &&
    !isPb() &&
    isMobile() &&
    flights_funnel_web_condensed_itinerary();

  return (
    <div
      className={mcn(
        styles.root,
        isCondencedItinerary && styles.rootSmaller,
        shouldWarn ? styles.calmWarning : shouldWarnDestructively ? styles.warning : undefined
      )}
      data-testid="timeline_stop"
    >
      {isVirtualInterliningProtectedLeg && (
        <Title
          variant="strong_2"
          title={i18n.trans(t("flights_selftransfer_title_during_layover"))}
          className={styles.title}
        />
      )}

      <Item
        isLastItem={!isRegularChangeOfAirports && !isVirtualInterliningProtectedLeg}
        icon={<Icon svg={ClockIcon} size="small" color="neutral_alt" />}
        badge={isCondencedItinerary}
        svgIcon={ClockIcon}
        text={i18n.trans(
          t("flights_itinerary_layover", {
            variables: { duration: formattingUtils.flightDuration(durationMinutes, i18n) }
          })
        )}
        ariaLabel={i18n.trans(
          t("flights_itinerary_layover", {
            variables: { duration: formattingUtils.flightDurationAria(durationMinutes, i18n) }
          })
        )}
      />

      {isRegularChangeOfAirports && !shouldShowVIChangeAirport && (
        <Item
          isLastItem={!isVirtualInterliningProtectedLeg}
          icon={<Icon svg={AirportIcon} size="small" color="neutral_alt" />}
          text={i18n.trans(t("flights_change_airport"))}
        />
      )}

      {isVirtualInterliningProtectedLeg && (
        <Item
          icon={<Icon svg={TravelInsuranceIcon} size="small" color="neutral_alt" />}
          text={i18n.trans(t("flights_selftransfer_guarantee_protected"))}
        />
      )}

      {shouldShowVIChangeAirport && (
        <Item
          icon={<Icon svg={AirportIcon} size="small" color="neutral_alt" />}
          text={i18n.trans(
            t("flights_airport_change_details", {
              variables: {
                city_name: prevDestination.city,
                airport_name1: prevDestination.airport,
                airport_name2: nextSource.airport
              }
            })
          )}
        />
      )}

      {isVICollectBaggage && prevDestination && (
        <Item
          icon={<Icon svg={BagHoldIcon} size="small" color="neutral_alt" />}
          text={i18n.trans(t("flights_selftransfer_collect_bags_next"))}
        />
      )}

      {isVirtualInterliningProtectedLeg && (
        <Link
          text={i18n.trans(t("flights_selftransfer_what_is_it_cta"))}
          href={`${VI_TERMS_AND_CONDITIONS_LINK}&lang=${i18n.language}`}
          attributes={{ target: "_blank" }}
        ></Link>
      )}

      {layoverType === "CHANGE_OF_GAUGE" && (
        <Item
          isLastItem={!isVirtualInterliningProtectedLeg}
          icon={<Icon svg={PlaneTripIcon} size="small" color="neutral_alt" />}
          text={i18n.trans(t("flights_change_aircraft"))}
        />
      )}
    </div>
  );
};

export const Item: FC<{
  isLastItem?: boolean;
  icon: ReactNode;
  text: ReactNode;
  ariaLabel?: string;
  badge?: boolean;
  svgIcon?: SVG;
}> = ({ isLastItem, icon, text, ariaLabel, badge, svgIcon }) => {
  return badge ? (
    <div>
      <Badge alternative icon={svgIcon} text={text} />
    </div>
  ) : (
    <div className={mcn(styles.item, isLastItem && styles.lastItem, styles.compactItem)}>
      <Frame direction="row" alignItems="center">
        <Frame attributes={{ style: { display: "flex", flex: 0 } }}>{icon}</Frame>
        <Frame attributes={{ style: { display: "flex", flex: 1 } }}>
          <Text className={styles.itemText} attributes={{ "aria-hidden": "true" }} variant="body_2">
            {text}
          </Text>
          <HiddenVisually>{ariaLabel}</HiddenVisually>
        </Frame>
      </Frame>
    </div>
  );
};
